import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAdminAuth } from "./AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Inbox,
  Phone,
  Mail,
  Send,
  Loader2,
  Plus,
  MessageSquare,
  Search,
  RefreshCw,
  Trash2,
  User as UserIcon,
  Paperclip,
} from "lucide-react";

const REFRESH_MS = 10000;

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
}

function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function conversationLabel(thread) {
  const base = thread.name || thread.phone || thread.email || "Unknown";
  const channel = (thread.last_channel || (thread.email ? "email" : "sms")).toUpperCase();
  return `${base}-${channel}`;
}

export default function AdminConversations() {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Composer state (reply)
  const [replyBody, setReplyBody] = useState("");
  const [replyChannels, setReplyChannels] = useState({ sms: true, email: false });
  const [replySubject, setReplySubject] = useState("");
  const [sending, setSending] = useState(false);

  // New message modal
  const [composeOpen, setComposeOpen] = useState(false);

  const scrollRef = useRef(null);

  const loadThreads = async () => {
    try {
      const r = await authAxios.get("/conversations");
      setThreads(r.data || []);
    } catch {
      // ignore polling errors
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (key) => {
    if (!key) return;
    setMessagesLoading(true);
    try {
      const r = await authAxios.get(
        `/conversations/${encodeURIComponent(key)}/messages`
      );
      setMessages(r.data || []);
    } catch {
      // ignore
    } finally {
      setMessagesLoading(false);
    }
  };

  // Initial load + polling
  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!selectedKey) return;
    setReplySubject("");
    setReplyBody("");
    loadMessages(selectedKey);
    const t = setInterval(() => loadMessages(selectedKey), REFRESH_MS);
    return () => clearInterval(t);
  }, [selectedKey]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const filteredThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(q) ||
        (t.phone || "").toLowerCase().includes(q) ||
        (t.email || "").toLowerCase().includes(q) ||
        (t.last_message_preview || "").toLowerCase().includes(q)
    );
  }, [threads, search]);

  const selectedThread = useMemo(
    () => threads.find((t) => t.thread_key === selectedKey),
    [threads, selectedKey]
  );

  const sendReply = async () => {
    if (sending || !selectedThread) return;
    const channels = [];
    if (replyChannels.sms && selectedThread.phone) channels.push("sms");
    if (replyChannels.email && selectedThread.email) channels.push("email");
    if (!channels.length) {
      toast({
        title: "Pick a channel",
        description: "Customer has no SMS or email available for the selected channels.",
        variant: "destructive",
      });
      return;
    }
    if (!replyBody.trim()) {
      toast({ title: "Message body required", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      await authAxios.post(
        `/conversations/${encodeURIComponent(selectedThread.thread_key)}/reply`,
        {
          channels,
          subject: replySubject || "",
          body: replyBody.trim(),
        }
      );
      setReplyBody("");
      setReplySubject("");
      toast({ title: "Message sent" });
      await loadMessages(selectedThread.thread_key);
      await loadThreads();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast({ title: "Send failed", description: String(detail), variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const deleteThread = async () => {
    if (!selectedThread) return;
    if (!window.confirm("Delete this entire conversation? This cannot be undone.")) return;
    try {
      await authAxios.delete(
        `/conversations/${encodeURIComponent(selectedThread.thread_key)}`
      );
      setSelectedKey(null);
      setMessages([]);
      await loadThreads();
      toast({ title: "Conversation deleted" });
    } catch (e) {
      toast({ title: "Delete failed", description: String(e?.message), variant: "destructive" });
    }
  };

  const openAttachment = async (message, attachment) => {
    const emailId = message?.meta?.resend_email_id;
    if (!emailId || !attachment?.id) {
      toast({ title: "Attachment unavailable", variant: "destructive" });
      return;
    }
    try {
      const res = await authAxios.get(
        `/email/attachments/${encodeURIComponent(emailId)}/${encodeURIComponent(attachment.id)}`
      );
      if (res.data?.download_url) {
        window.open(res.data.download_url, "_blank", "noopener,noreferrer");
        return;
      }
      toast({ title: "Attachment unavailable", variant: "destructive" });
    } catch (e) {
      toast({
        title: "Attachment unavailable",
        description: String(e?.response?.data?.detail || e.message),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="h-screen flex flex-col" data-testid="admin-conversations">
      <header className="px-6 py-4 border-b bg-white flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-extrabold text-[#07304f] flex items-center gap-2">
            <Inbox size={20} /> Conversations
          </h1>
          <p className="text-xs text-slate-500">
            Two-way SMS &amp; email with customers. Refreshes every {REFRESH_MS / 1000}s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              loadThreads();
              if (selectedKey) loadMessages(selectedKey);
            }}
            data-testid="refresh-button"
          >
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
          <Button
            onClick={() => setComposeOpen(true)}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="new-message-button"
          >
            <Plus size={16} className="mr-1.5" /> New Message
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Thread list */}
        <aside className="w-80 border-r bg-white flex flex-col">
          <div className="p-3 border-b">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                placeholder="Search name, phone, email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 text-sm"
                data-testid="search-threads-input"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div className="p-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </div>
            )}
            {!loading && filteredThreads.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500">
                <MessageSquare size={28} className="mx-auto mb-2 text-slate-300" />
                No conversations yet. Click <strong>New Message</strong> to start one.
              </div>
            )}
            {filteredThreads.map((t) => {
              const active = t.thread_key === selectedKey;
              return (
                <button
                  key={t.thread_key}
                  onClick={() => setSelectedKey(t.thread_key)}
                  className={`w-full text-left px-4 py-3 border-b transition-colors ${
                    active ? "bg-sky-50 border-l-4 border-l-sky-500" : "hover:bg-slate-50"
                  }`}
                  data-testid={`thread-${t.thread_key}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div
                      className="font-semibold text-sm text-slate-800 truncate"
                      data-testid={`thread-label-${t.thread_key}`}
                    >
                      {conversationLabel(t)}
                    </div>
                    <div className="text-[10px] text-slate-400 shrink-0 ml-2">
                      {timeAgo(t.last_message_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1">
                    {t.phone && (
                      <span className="inline-flex items-center gap-0.5">
                        <Phone size={10} /> {t.phone}
                      </span>
                    )}
                    {t.email && (
                      <span className="inline-flex items-center gap-0.5 truncate">
                        <Mail size={10} /> {t.email}
                      </span>
                    )}
                  </div>
                  <div className="flex items-start gap-2">
                    <p className="text-xs text-slate-600 line-clamp-2 flex-1">
                      {t.last_direction === "outbound" && (
                        <span className="text-slate-400 mr-1">You:</span>
                      )}
                      {t.last_message_preview || "—"}
                    </p>
                    {t.unread_count > 0 && (
                      <span className="bg-sky-600 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">
                        {t.unread_count}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Conversation pane */}
        <section className="flex-1 flex flex-col bg-slate-50">
          {!selectedThread ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 flex-col gap-3">
              <MessageSquare size={48} className="text-slate-300" />
              <div>Select a conversation to view messages</div>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="px-6 py-4 bg-white border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#0a4f8a] text-white flex items-center justify-center font-bold">
                    {(selectedThread.name ||
                      selectedThread.phone ||
                      selectedThread.email ||
                      "?")
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                  <div>
                    <div className="font-bold text-slate-800" data-testid="selected-thread-label">
                      {conversationLabel(selectedThread)}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-3">
                      {selectedThread.phone && (
                        <a
                          href={`tel:${selectedThread.phone}`}
                          className="inline-flex items-center gap-1 hover:text-sky-600"
                        >
                          <Phone size={11} /> {selectedThread.phone}
                        </a>
                      )}
                      {selectedThread.email && (
                        <a
                          href={`mailto:${selectedThread.email}`}
                          className="inline-flex items-center gap-1 hover:text-sky-600"
                        >
                          <Mail size={11} /> {selectedThread.email}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deleteThread}
                  className="text-red-600 hover:text-red-700 hover:border-red-300"
                  data-testid="delete-thread-button"
                >
                  <Trash2 size={14} className="mr-1" /> Delete
                </Button>
              </div>

              {/* Messages */}
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-6 space-y-3"
                data-testid="messages-pane"
              >
                {messagesLoading && messages.length === 0 && (
                  <div className="text-center text-sm text-slate-400 flex items-center justify-center gap-2 py-6">
                    <Loader2 size={14} className="animate-spin" /> Loading messages…
                  </div>
                )}
                {messages.map((m) => {
                  const outbound = m.direction === "outbound";
                  return (
                    <div
                      key={m.id}
                      className={`flex ${outbound ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                          outbound
                            ? "bg-sky-600 text-white rounded-br-sm"
                            : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
                        }`}
                      >
                        {m.subject && m.channel === "email" && (
                          <div
                            className={`text-xs font-bold mb-1 ${
                              outbound ? "text-sky-100" : "text-slate-600"
                            }`}
                          >
                            {m.subject}
                          </div>
                        )}
                        <div className="whitespace-pre-wrap text-sm leading-relaxed">
                          {m.body}
                        </div>
                        {Array.isArray(m.meta?.attachments) && m.meta.attachments.length > 0 && (
                          <div className="mt-2 space-y-1" data-testid={`message-attachments-${m.id}`}>
                            {m.meta.attachments.map((attachment) => (
                              <button
                                key={attachment.id}
                                type="button"
                                onClick={() => openAttachment(m, attachment)}
                                className={`flex max-w-full items-center gap-1.5 rounded-md border px-2 py-1 text-left text-xs transition-colors ${
                                  outbound
                                    ? "border-sky-200/40 bg-sky-500/30 text-white hover:bg-sky-500/50"
                                    : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                                }`}
                                data-testid={`open-attachment-${m.id}-${attachment.id}`}
                              >
                                <Paperclip size={12} />
                                <span className="truncate">{attachment.filename || "attachment"}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        <div
                          className={`flex items-center gap-2 mt-1.5 text-[10px] ${
                            outbound ? "text-sky-100/80" : "text-slate-400"
                          }`}
                        >
                          {m.channel === "sms" ? (
                            <Phone size={10} />
                          ) : (
                            <Mail size={10} />
                          )}
                          <span>{m.channel.toUpperCase()}</span>
                          <span>•</span>
                          <span>{formatTime(m.created_at)}</span>
                          {m.status === "failed" && (
                            <span className="ml-1 px-1.5 rounded bg-red-500/30 text-red-100 font-semibold">
                              Failed
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Composer */}
              <div className="border-t bg-white p-4 space-y-3">
                <div className="flex items-center gap-4 text-sm">
                  {selectedThread.phone && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={replyChannels.sms}
                        onCheckedChange={(v) =>
                          setReplyChannels((c) => ({ ...c, sms: !!v }))
                        }
                        data-testid="reply-channel-sms"
                      />
                      <span className="inline-flex items-center gap-1 text-slate-700">
                        <Phone size={13} /> SMS
                      </span>
                    </label>
                  )}
                  {selectedThread.email && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={replyChannels.email}
                        onCheckedChange={(v) =>
                          setReplyChannels((c) => ({ ...c, email: !!v }))
                        }
                        data-testid="reply-channel-email"
                      />
                      <span className="inline-flex items-center gap-1 text-slate-700">
                        <Mail size={13} /> Email
                      </span>
                    </label>
                  )}
                </div>
                {replyChannels.email && selectedThread.email && (
                  <Input
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                    placeholder="Subject (email only) — leave blank for default"
                    className="h-9 text-sm"
                    data-testid="reply-subject-input"
                  />
                )}
                <div className="flex gap-2">
                  <Textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Type your message…"
                    rows={2}
                    className="flex-1 resize-none"
                    data-testid="reply-body-input"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                  />
                  <Button
                    onClick={sendReply}
                    disabled={sending || !replyBody.trim()}
                    className="bg-sky-600 hover:bg-sky-700 text-white self-end h-10"
                    data-testid="send-reply-button"
                  >
                    {sending ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                  </Button>
                </div>
                <div className="text-[11px] text-slate-400">
                  Tip: ⌘ / Ctrl + Enter to send
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      <NewMessageDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        onSent={async (threadKey) => {
          await loadThreads();
          setSelectedKey(threadKey);
        }}
      />
    </div>
  );
}

function NewMessageDialog({ open, onOpenChange, onSent }) {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [channels, setChannels] = useState({ sms: true, email: false });
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setPhone("");
      setEmail("");
      setChannels({ sms: true, email: false });
      setSubject("");
      setBody("");
      setSending(false);
    }
  }, [open]);

  const send = async () => {
    const ch = [];
    if (channels.sms) ch.push("sms");
    if (channels.email) ch.push("email");
    if (!ch.length) {
      toast({ title: "Pick at least one channel", variant: "destructive" });
      return;
    }
    if (channels.sms && !phone.trim()) {
      toast({ title: "Phone required for SMS", variant: "destructive" });
      return;
    }
    if (channels.email && !email.trim()) {
      toast({ title: "Email required for email", variant: "destructive" });
      return;
    }
    if (!body.trim()) {
      toast({ title: "Message body required", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const res = await authAxios.post("/conversations/send", {
        channels: ch,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        subject: subject.trim(),
        body: body.trim(),
      });
      toast({ title: "Message sent" });
      const sentMsg = (res.data || [])[0];
      onOpenChange(false);
      if (sentMsg && onSent) onSent(sentMsg.thread_key);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast({ title: "Send failed", description: String(detail), variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-[#07304f] flex items-center gap-2">
            <Plus size={18} /> New Message
          </DialogTitle>
          <DialogDescription>
            Send an SMS, email, or both to start a new conversation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Customer name (optional)</Label>
            <div className="relative">
              <UserIcon
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Smith"
                className="h-10 pl-9"
                data-testid="compose-name-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Phone</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0412 345 678"
                className="h-10"
                data-testid="compose-phone-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Email</Label>
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@email.com"
                type="email"
                className="h-10"
                data-testid="compose-email-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={channels.sms}
                onCheckedChange={(v) => setChannels((c) => ({ ...c, sms: !!v }))}
                data-testid="compose-channel-sms"
              />
              <span className="inline-flex items-center gap-1 text-slate-700">
                <Phone size={13} /> SMS
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={channels.email}
                onCheckedChange={(v) => setChannels((c) => ({ ...c, email: !!v }))}
                data-testid="compose-channel-email"
              />
              <span className="inline-flex items-center gap-1 text-slate-700">
                <Mail size={13} /> Email
              </span>
            </label>
          </div>
          {channels.email && (
            <div>
              <Label className="text-xs font-semibold">Email subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject (optional)"
                className="h-10"
                data-testid="compose-subject-input"
              />
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold">Message</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder="Type your message…"
              data-testid="compose-body-input"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              onClick={send}
              disabled={sending}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="compose-send-button"
            >
              {sending ? (
                <>
                  <Loader2 size={14} className="mr-1.5 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Send size={14} className="mr-1.5" /> Send
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
